import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Paper, Tabs, Tab, Box, Button, Typography, Grid } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../contexts/AuthContext';

/**
 * Top navigation bar for the document review system.
 *
 * Purpose: Provides quick navigation between Slack messages and Zendesk ticket
 * viewer, and a logout control. Matches the revised product scope with a
 * dedicated "Zendesk Tickets" tab.
 */
const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    navigate(newValue);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  /**
   * Navigate to the Slack RAG app hosted under /rag.
   *
   * Purpose in the broader system:
   * - Provides a clear affordance for authenticated users to jump into the
   *   RAG experience, which is a separate SPA mounted at /rag behind Nginx.
   * - We use a full navigation to ensure Nginx serves the other bundle.
   */
  const goToRag = () => {
    window.location.assign('/rag/');
  };

  return (
    <Paper sx={{ mb: 3 }}>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={8}>
            <Typography variant="h5" color="primary" sx={{ mb: 1 }}>Document Review System</Typography>
            {/**
             * Use standard Tabs to avoid MUI's ScrollbarSize measurement.
             * Reason: The scrollable variant measures a hidden element's offsetHeight
             * on mount which can race with ref attachment, triggering a null ref
             * error ("offsetHeight of null") in ScrollbarSize.js in some browsers.
             */}
            <Tabs value={location.pathname} onChange={handleTabChange} variant="standard">
              <Tab label="Slack Messages" value="/messages" sx={{ textTransform: 'none', fontSize: '16px' }} />
              <Tab label="Zendesk Tickets" value="/zendesk" sx={{ textTransform: 'none', fontSize: '16px' }} />
            </Tabs>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="contained" color="primary" onClick={goToRag} sx={{ height: '40px' }}>Go to RAG</Button>
              <Button variant="outlined" color="primary" startIcon={<LogoutIcon />} onClick={handleLogout} sx={{ height: '40px' }}>Logout</Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default Navigation;


